import { Button, Drawer, Flex, Select, Typography } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getAlbums } from "../../Album/api/album.services.ts"
import { getAttachmentsFilterOptions } from "../../Album/api/attachment.services.ts"
import { type AttachmentFilterOptions } from "../../Album/types/attachment.types.ts"
import { getFolders } from "../api/folder.services.ts"
import {
  EMPTY_FILES_FILTERS,
  type FilesFilters
} from "../types/files-filters.types.ts"
import { type FolderListItem } from "../types/folder.types.ts"

const { Text } = Typography

interface FilesFilterDrawerProps {
  open: boolean
  filters: FilesFilters
  showRelatedFilter: boolean
  /** Hides the Folder filter — the user is already browsing inside one. */
  hideFolderFilter?: boolean
  projectId: string
  owners: string[]
  onClose: () => void
  onChange: (filters: FilesFilters) => void
  /** Lets the parent resolve filter values to labels for the chips row. */
  onOptionsLoaded?: (options: {
    fileTypes: { label: string; value: string }[]
    folders: { label: string; value: string }[]
    albums: { label: string; value: string }[]
  }) => void
}

export function FilesFilterDrawer({
  open,
  filters,
  showRelatedFilter,
  hideFolderFilter = false,
  projectId,
  owners,
  onClose,
  onChange,
  onOptionsLoaded
}: FilesFilterDrawerProps) {
  const { t } = useTranslation()
  const [options, setOptions] = useState<AttachmentFilterOptions | null>(null)
  const [folders, setFolders] = useState<FolderListItem[]>([])
  const [albums, setAlbums] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    if (!open || options) return
    let cancelled = false
    void getAttachmentsFilterOptions().then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setOptions(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [open, options])

  useEffect(() => {
    if (!open || !projectId) return
    let cancelled = false
    void getFolders({ project: projectId }).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setFolders(response.data.results || [])
    })
    void getAlbums({ project: projectId, page_size: 100 }).then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setAlbums(response.data.results || [])
    })
    return () => {
      cancelled = true
    }
  }, [open, projectId])

  useEffect(() => {
    if (!options && !folders.length && !albums.length) return
    onOptionsLoaded?.({
      fileTypes: (options?.file_types ?? []).map((option) => ({
        label: String(option.label ?? option.value ?? ""),
        value: String(option.value ?? "")
      })),
      folders: folders.map((folder) => ({
        label: folder.name,
        value: folder.id
      })),
      albums: albums.map((album) => ({ label: album.name, value: album.id }))
    })
  }, [options, folders, albums, onOptionsLoaded])

  const set = (key: keyof FilesFilters) => (value: string | undefined) =>
    onChange({ ...filters, [key]: value ?? null })

  const sections: Array<{
    key: keyof FilesFilters
    label: string
    options: BaseOptionType[] | undefined
    visible: boolean
  }> = [
    {
      key: "file_type",
      label: t("files.filters.fileType"),
      options: options?.file_types,
      visible: true
    },
    {
      key: "folder",
      label: t("files.filters.folder"),
      options: folders.map((folder) => ({
        label: folder.name,
        value: folder.id
      })),
      visible: !hideFolderFilter
    },
    {
      key: "album",
      label: t("files.filters.album"),
      options: albums.map((album) => ({ label: album.name, value: album.id })),
      visible: true
    },
    {
      key: "owner",
      label: t("files.filters.owner"),
      options: owners.map((owner) => ({ label: owner, value: owner })),
      visible: owners.length > 0
    },
    {
      key: "model_type",
      label: t("files.filters.relatedTo"),
      options: options?.model_types,
      visible: showRelatedFilter
    },
    {
      key: "date_range",
      label: t("files.filters.date"),
      options: options?.date_ranges,
      visible: true
    }
  ]

  const activeCount = Object.values(filters).filter((value) => value).length

  return (
    <Drawer
      title={t("files.filters.title")}
      placement="right"
      width={360}
      open={open}
      onClose={onClose}
      extra={
        activeCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={() => onChange(EMPTY_FILES_FILTERS)}
          >
            {t("files.filters.clearAll")}
          </Button>
        )
      }
    >
      <Flex vertical gap="large">
        {sections
          .filter((section) => section.visible)
          .map((section) => (
            <Flex vertical gap={8} key={section.key}>
              <Text strong>{section.label}</Text>
              <Select<string>
                allowClear
                aria-label={section.label}
                placeholder={t("files.filters.any")}
                value={filters[section.key] ?? undefined}
                onChange={set(section.key)}
                options={section.options ?? []}
                className="w-full"
              />
            </Flex>
          ))}
      </Flex>
    </Drawer>
  )
}
